import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  ReactNode,
} from "react";
import { UserGamificationDetails } from "../../../types/welcome";
import { ComponentType } from "../../../types/Questionnaire";

export type HeaderLeftContent = "logo" | "back" | "none";
export type HeaderRightContent = "menu" | "settings" | "none";

interface ResonanceLayoutValue {
  disableHeader: boolean;
  disableNav: boolean;
  isMenuVisible: boolean;
  backgroundColor: string;
  title: string;
  userGamificationDetails: UserGamificationDetails;
  welcomePage: boolean;
  questionnaires: ComponentType[];
  headerBackground: string;
  headerLeftContent: HeaderLeftContent;
  headerRightContent: HeaderRightContent;
  headerBackFallbackPath: string;
}

interface ResonanceLayoutContextType extends ResonanceLayoutValue {
  setResonanceLayout: (
    updates: Partial<ResonanceLayoutValue>
  ) => void;
}

// 🔸 Default state values
const ResonanceLayoutDefaultStateValue: ResonanceLayoutValue = {
  disableHeader: false,
  disableNav: false,
  isMenuVisible: false,
  backgroundColor: "white",
  title: "default_title",
  userGamificationDetails: undefined,
  welcomePage: false,
  questionnaires: undefined,
  headerBackground: "transparent",
  headerLeftContent: "logo",
  headerRightContent: "menu",
  headerBackFallbackPath: "",
};

// 🔸 Fallback context value
const ResonanceLayoutDefaultValue: ResonanceLayoutContextType = {
  ...ResonanceLayoutDefaultStateValue,
  setResonanceLayout: () =>
    console.warn("ResonanceLayoutContext is not initialized"),
};

// 🔸 Create context
const ResonanceLayoutContext = createContext<ResonanceLayoutContextType>(
  ResonanceLayoutDefaultValue
);

// 🔸 Provider
const ResonanceLayoutProvider: React.FC<{
  children: ReactNode;
  initialLayoutConfig?: Partial<ResonanceLayoutValue>;
}> = ({ children, initialLayoutConfig }) => {
  const [state, setState] = useState<ResonanceLayoutValue>({
    ...ResonanceLayoutDefaultStateValue,
    ...initialLayoutConfig, // ✅ preload layout config on first render
  });

    // Stable function identity!
  const setResonanceLayout = useMemo(
    () => (updates: Partial<ResonanceLayoutValue>) => {
      setState(prev => ({ ...prev, ...updates }));
    },
    []
  );

  const contextValue = useMemo(
    () => ({
      ...state,
      setResonanceLayout,
    }),
      [state, setResonanceLayout] // Add setResonanceLayout!
  );

  return (
    <ResonanceLayoutContext.Provider value={contextValue}>
      {children}
    </ResonanceLayoutContext.Provider>
  );
};

// 🔸 Hook to access layout state
const useResonanceLayout = () => useContext(ResonanceLayoutContext);

// 🔸 Hook to set and clean up layout properties on mount/unmount
const useSetResonanceLayout = (
  updates: Partial<ResonanceLayoutValue>
) => {
  const { setResonanceLayout } = useResonanceLayout();

  useEffect(() => {
    // Set desired layout values
    setResonanceLayout(updates);

    // Reset only the same keys back to default values
    return () => {
      const reset: Partial<ResonanceLayoutValue> = {};
      for (const key in updates) {
        reset[key] = ResonanceLayoutDefaultStateValue[key];
      }
      setResonanceLayout(reset);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};

export {
  ResonanceLayoutProvider,
  useResonanceLayout,
  useSetResonanceLayout,
  ResonanceLayoutContext,
};
